/**
 * Chapter007.js — Safe Work Method Statement (OHSE 007)
 * Migrated from SWMS Platform Chapter007.tsx → JavaScript
 *
 * Props:
 *   swmsData   {object}  full swmsData from SWMSTab state
 *   onChange   {fn}      (field, value) => void — SWMSTab.handleChange
 *   companyInfo {object} from settings API { name, acn, address, phone, email }
 *   projectInfo {object} derived { project_name, state, project_type, ... }
 */

import { useState, useCallback, useMemo } from 'react';
import { Plus, Trash2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white min-h-[60px] resize-y transition-all';
const selectCls = 'w-full px-2 py-1.5 text-sm bg-white border border-[#e0dbd0] rounded text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c] transition-all';

// ── Risk level helpers ───────────────────────────────────────────────────────

function riskColor(level) {
  if (level === 1) return 'bg-red-100 text-red-800 border-red-300';
  if (level === 2) return 'bg-amber-100 text-amber-800 border-amber-300';
  return 'bg-green-100 text-green-800 border-green-300';
}

function riskLabel(level) {
  if (level === 1) return '1 — High';
  if (level === 2) return '2 — Medium';
  return '3 — Low';
}

// ── Editable list (training items / plant & equipment / legislation) ──────────

function EditableList({ title, items = [], onChange, placeholder = 'Add item...' }) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setInput('');
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const update = (idx, val) => {
    const next = [...items];
    next[idx] = val;
    onChange(next);
  };

  return (
    <div>
      <p className="text-xs font-semibold text-[#1a1a2e] uppercase tracking-wide mb-2">{title}</p>
      <div className="space-y-1 mb-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input type="text" value={item}
              onChange={e => update(idx, e.target.value)}
              className={inputCls + ' flex-1'} />
            <button type="button" onClick={() => remove(idx)}
              className="text-red-400 hover:text-red-600 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={placeholder} className={inputCls + ' flex-1'} />
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-[#c9a84c] border border-[#c9a84c]/40 rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

// ── Work Step Row ────────────────────────────────────────────────────────────

function WorkStepRow({ step, index, onChange, onDelete }) {
  return (
    <tr className="border-b border-[#e0dbd0] bg-white hover:bg-[#f8f7f4]">
      <td className="px-2 py-2 text-center text-xs text-gray-400">{index + 1}</td>
      <td className="px-2 py-2">
        <textarea value={step.operation || ''} rows={2}
          onChange={e => onChange({ ...step, operation: e.target.value })}
          className={textareaCls} placeholder="Operation..." />
      </td>
      <td className="px-2 py-2">
        <textarea value={step.hazard || ''} rows={2}
          onChange={e => onChange({ ...step, hazard: e.target.value })}
          className={textareaCls} placeholder="Hazard..." />
      </td>
      <td className="px-2 py-2 text-center">
        <select value={step.risk_level ?? 2}
          onChange={e => onChange({ ...step, risk_level: Number(e.target.value) })}
          className={`text-xs rounded border px-1 py-0.5 ${riskColor(step.risk_level ?? 2)}`}>
          <option value={1}>1 — High</option>
          <option value={2}>2 — Medium</option>
          <option value={3}>3 — Low</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <textarea value={step.controls || ''} rows={2}
          onChange={e => onChange({ ...step, controls: e.target.value })}
          className={textareaCls} placeholder="Controls to implement..." />
      </td>
      <td className="px-2 py-2 text-center">
        <select value={step.new_risk_level ?? 3}
          onChange={e => onChange({ ...step, new_risk_level: Number(e.target.value) })}
          className={`text-xs rounded border px-1 py-0.5 ${riskColor(step.new_risk_level ?? 3)}`}>
          <option value={1}>1 — High</option>
          <option value={2}>2 — Medium</option>
          <option value={3}>3 — Low</option>
        </select>
      </td>
      <td className="px-2 py-2">
        <input type="text" value={step.responsible_person || ''}
          onChange={e => onChange({ ...step, responsible_person: e.target.value })}
          className={inputCls} placeholder="Person responsible" />
      </td>
      <td className="px-2 py-2 text-center">
        <button type="button" onClick={onDelete} className="text-red-400 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// ── Training Register Row ────────────────────────────────────────────────────

function TrainingRegRow({ reg, onChange, onDelete }) {
  return (
    <tr className="border-b border-[#e0dbd0] bg-white">
      <td className="px-3 py-2">
        <input type="text" value={reg.name || ''} placeholder="Name"
          onChange={e => onChange({ ...reg, name: e.target.value })}
          className={inputCls} />
      </td>
      <td className="px-3 py-2">
        <input type="text" value={reg.position_role || ''} placeholder="Role on site"
          onChange={e => onChange({ ...reg, position_role: e.target.value })}
          className={inputCls} />
      </td>
      <td className="px-3 py-2">
        <input type="text" value={reg.signature || ''} placeholder="Signature (text)"
          onChange={e => onChange({ ...reg, signature: e.target.value })}
          className={inputCls} />
      </td>
      <td className="px-3 py-2">
        <input type="date" value={reg.date || ''}
          onChange={e => onChange({ ...reg, date: e.target.value })}
          className={inputCls} />
      </td>
      <td className="px-3 py-2 text-center">
        <button type="button" onClick={onDelete} className="text-red-400 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export default function Chapter007({ swmsData = {}, onChange, companyInfo = {}, projectInfo = {} }) {
  const steps = swmsData.swms_steps || [];
  const trainingRegs = swmsData.training_entries || [];
  const ppeLists = swmsData.swms_ppe_lists || { always_worn: [], as_required: [] };
  const training = swmsData.training_required || [];
  const plantEquip = swmsData.plant_equipment || [];
  const legislation = swmsData.legislation || [];
  const swmsNumber = swmsData.swms_number || '';

  const siteManagerEmpty = !swmsData.site_manager_name && !swmsData.site_manager_phone && !swmsData.site_manager_email;

  const addStep = () => {
    onChange('swms_steps', [...steps, {
      id: crypto.randomUUID(),
      operation: '',
      hazard: '',
      risk_level: 2,
      controls: '',
      new_risk_level: 3,
      responsible_person: '',
    }]);
  };

  const updateStep = (idx, updated) => {
    const next = [...steps];
    next[idx] = updated;
    onChange('swms_steps', next);
  };

  const deleteStep = (idx) => onChange('swms_steps', steps.filter((_, i) => i !== idx));

  const addTraining = () => {
    onChange('training_entries', [...trainingRegs, {
      id: crypto.randomUUID(),
      name: '',
      position_role: '',
      signature: '',
      date: '',
    }]);
  };

  const updateTraining = (idx, updated) => {
    const next = [...trainingRegs];
    next[idx] = updated;
    onChange('training_entries', next);
  };

  const deleteTraining = (idx) => onChange('training_entries', trainingRegs.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6 text-[#1a1a2e]">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 007</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Safe Work Method Statement (SWMS)
        </h2>
      </div>

      {/* Section A: SWMS Number */}
      <Card title="SWMS Identification">
        <div className="grid grid-cols-2 gap-4">
          <Field label="SWMS Number">
            <input type="text" value={swmsNumber}
              onChange={e => onChange('swms_number', e.target.value)}
              className={inputCls} placeholder="e.g. 001 - Shopfitting" />
          </Field>
        </div>
        {/* Company info display (read-only, from settings) */}
        <div className="mt-4 p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm space-y-1">
          <p className="font-semibold text-[#c9a84c]">{companyInfo.name || '[Company Name]'}</p>
          {companyInfo.acn && <p className="text-gray-500"><span className="font-medium text-[#1a1a2e] w-12 inline-block">ABN:</span>{companyInfo.acn}</p>}
          {companyInfo.address && <p className="text-gray-500"><span className="font-medium text-[#1a1a2e] w-12 inline-block">Addr:</span>{companyInfo.address}</p>}
          {companyInfo.phone && <p className="text-gray-500"><span className="font-medium text-[#1a1a2e] w-12 inline-block">Phone:</span>{companyInfo.phone}</p>}
          <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-[#e0dbd0]">Company details managed in Settings.</p>
        </div>
      </Card>

      {/* Section B: Site Manager (editable in SWMS Details above, shown here read-only) */}
      <Card title="Site Manager Details">
        {siteManagerEmpty && (
          <div className="mb-3 flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span><strong>Required:</strong> Fill in site manager details in the SWMS Details section above.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-400">Name: </span><span>{swmsData.site_manager_name || '—'}</span></div>
          <div><span className="text-gray-400">Phone: </span><span>{swmsData.site_manager_phone || '—'}</span></div>
          <div><span className="text-gray-400">Email: </span><span>{swmsData.site_manager_email || '—'}</span></div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Edit these fields in the "SWMS Details" section above.</p>
      </Card>

      {/* Section C: Training & Equipment */}
      <Card title="Training & Equipment">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <EditableList
            title="Training required prior to commencement"
            items={training}
            onChange={v => onChange('training_required', v)}
            placeholder="Add training requirement..."
          />
          <EditableList
            title="Plant and equipment that may be used"
            items={plantEquip}
            onChange={v => onChange('plant_equipment', v)}
            placeholder="Add plant/equipment..."
          />
        </div>
      </Card>

      {/* Section D: Development Note */}
      <Card title="SWMS Development Note">
        <div className="text-sm leading-relaxed space-y-3">
          <p>
            SWMS has been developed using the hierarchy of controls to reduce the risk and likelihood of an incident/injury occurring.
            The use of <em>Elimination, Substitution, Engineering/isolation, Administrative and PPE</em> measures
            will be used in the above order of preference wherever practicable.
          </p>
          <p>
            The person(s) responsible for ensuring implementation, monitoring and compliance with the SWMS:{' '}
            <span className="font-semibold text-[#c9a84c]">{companyInfo.name || '[Company Name]'}</span> Site Foreman
          </p>
        </div>
      </Card>

      {/* Section E: Legislation */}
      <Card title="Legislation Used in the Development of this SWMS">
        <EditableList
          title="Legislation / Codes of Practice"
          items={legislation}
          onChange={v => onChange('legislation', v)}
          placeholder="Add legislation or code of practice..."
        />
      </Card>

      {/* Section F: Work Steps Table */}
      <Card title="Safe Work Method Statement — Operations">
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-[#1a1a2e] text-white">
                <th className="px-2 py-2.5 text-center text-xs uppercase tracking-wide w-[3%]">#</th>
                <th className="px-2 py-2.5 text-left text-xs uppercase tracking-wide w-[15%]">Operation</th>
                <th className="px-2 py-2.5 text-left text-xs uppercase tracking-wide w-[15%]">Hazard</th>
                <th className="px-2 py-2.5 text-center text-xs uppercase tracking-wide w-[8%]">Risk Level</th>
                <th className="px-2 py-2.5 text-left text-xs uppercase tracking-wide w-[30%]">Controls to Implement</th>
                <th className="px-2 py-2.5 text-center text-xs uppercase tracking-wide w-[8%]">New Risk</th>
                <th className="px-2 py-2.5 text-left text-xs uppercase tracking-wide w-[15%]">Responsible Person</th>
                <th className="px-2 py-2.5 w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, idx) => (
                <WorkStepRow
                  key={step.id}
                  step={step}
                  index={idx}
                  onChange={updated => updateStep(idx, updated)}
                  onDelete={() => deleteStep(idx)}
                />
              ))}
              {steps.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No work steps yet. Click "Add Operation" to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <button type="button" onClick={addStep}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
            <Plus className="w-4 h-4" /> Add Operation
          </button>
        </div>
      </Card>

      {/* Section G: Training Register */}
      <Card title={`Training Register${swmsNumber ? ` — SWMS No. ${swmsNumber}` : ''}`}
        description="By signing below, you acknowledge the SWMS is achievable and will be practiced on site.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1a1a2e] text-white">
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[25%]">Name</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[25%]">Position / Role</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[25%]">Signature</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide w-[20%]">Date</th>
                <th className="px-3 py-2.5 w-[5%]"></th>
              </tr>
            </thead>
            <tbody>
              {trainingRegs.map((reg, idx) => (
                <TrainingRegRow
                  key={reg.id}
                  reg={reg}
                  onChange={updated => updateTraining(idx, updated)}
                  onDelete={() => deleteTraining(idx)}
                />
              ))}
              {trainingRegs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    No training register entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <button type="button" onClick={addTraining}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] transition-colors">
            <Plus className="w-4 h-4" /> Add Training Register
          </button>
        </div>
      </Card>
    </div>
  );
}

// ── Shared card layout (local to this file) ──────────────────────────────────

function Card({ title, description, children }) {
  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6]">
        <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{label}</label>
      {children}
    </div>
  );
}
